import React from 'react';

import { Code } from '@atlaskit/code';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import AddIcon from '@atlaskit/icon/core/add';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import Lozenge from '@atlaskit/lozenge';
import { Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const iconContainerStyles = cssMap({
	root: {
		borderStyle: 'dashed',
		borderRadius: token('radius.small'),
		borderColor: token('color.border.accent.magenta'),
		borderWidth: token('border.width'),
	},
});

/**
 * 1:1 migration styles generated by the `next-icon-spacing-to-flex-primitive` codemod
 * and the `@atlaskit/design-system/no-icon-spacing-prop` ESLint rule suggestion.
 * Wrap icons in a `<Flex xcss={iconSpacingStyles.spaceXXX}>` to replace the deprecated `spacing` prop.
 * Where possible, prefer updating the parent component's padding instead.
 */
const iconSpacingStyles = cssMap({
	space050: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
	},
	space075: {
		paddingBlock: token('space.075'),
		paddingInline: token('space.075'),
	},
	space025: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.025'),
	},
});

const IconContainer = ({ children }: { children: React.ReactNode }) => (
	<Flex xcss={iconContainerStyles.root}>{children}</Flex>
);

const IconSpacingExample = (): JSX.Element => {
	return (
		<Stack space="space.400">
			{/* Medium icons */}
			<Stack space="space.150">
				<Heading size="small">
					Medium icon — <Code>spacing</Code> prop{' '}
					<Lozenge appearance="warning" isBold>
						Deprecated
					</Lozenge>
				</Heading>
				<Inline space="space.200" alignBlock="center">
					<IconContainer>
						<AddIcon label="" />
					</IconContainer>
					<IconContainer>
						{/* eslint-disable-next-line @atlaskit/design-system/no-icon-spacing-prop -- deprecated, shown for migration reference */}
						<AddIcon label="" spacing="spacious" />
					</IconContainer>
				</Inline>
			</Stack>

			<Stack space="space.150">
				<Heading size="small">Medium icon — Flex with padding (1:1 migration)</Heading>
				<Inline space="space.200" alignBlock="center">
					<IconContainer>
						<AddIcon label="" />
					</IconContainer>
					<IconContainer>
						<Flex xcss={iconSpacingStyles.space050}>
							<AddIcon label="" />
						</Flex>
					</IconContainer>
				</Inline>
			</Stack>

			{/* Small icons */}
			<Stack space="space.150">
				<Heading size="small">
					Small icon — <Code>spacing</Code> prop{' '}
					<Lozenge appearance="warning" isBold>
						Deprecated
					</Lozenge>
				</Heading>
				<Inline space="space.200" alignBlock="center">
					<IconContainer>
						<ChevronDownIcon label="" size="small" />
					</IconContainer>
					<IconContainer>
						{/* eslint-disable-next-line @atlaskit/design-system/no-icon-spacing-prop -- deprecated, shown for migration reference */}
						<ChevronDownIcon label="" size="small" spacing="compact" />
					</IconContainer>
					<IconContainer>
						{/* eslint-disable-next-line @atlaskit/design-system/no-icon-spacing-prop -- deprecated, shown for migration reference */}
						<ChevronDownIcon label="" size="small" spacing="spacious" />
					</IconContainer>
				</Inline>
			</Stack>

			<Stack space="space.150">
				<Heading size="small">Small icon — Flex with padding (1:1 migration)</Heading>
				<Inline space="space.200" alignBlock="center">
					<IconContainer>
						<ChevronDownIcon label="" size="small" />
					</IconContainer>
					<IconContainer>
						<Flex xcss={iconSpacingStyles.space025}>
							<ChevronDownIcon label="" size="small" />
						</Flex>
					</IconContainer>
					<IconContainer>
						<Flex xcss={iconSpacingStyles.space075}>
							<ChevronDownIcon label="" size="small" />
						</Flex>
					</IconContainer>
				</Inline>
			</Stack>
		</Stack>
	);
};

export default IconSpacingExample;
